import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useMe, useProjects, useCreateProject } from '../api/hooks.js';
import { useT } from '../i18n/useT.js';

export function ProjectsPage() {
  const all = useT();
  const t = all.projects;
  const common = all.common;
  const navigate = useNavigate();
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const { data: projects, isLoading } = useProjects(orgId);
  const createProject = useCreateProject(orgId);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  const create = async () => {
    await createProject.mutateAsync({ name });
    setOpen(false);
    setName('');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5">{t.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t.subtitle}
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpen(true)}>
          {t.newProject}
        </Button>
      </Box>

      {isLoading && <Typography color="text.secondary">{common.loading}</Typography>}
      {projects?.length === 0 && (
        <Typography color="text.secondary">{t.empty}</Typography>
      )}

      <Grid container spacing={2}>
        {projects?.map((p) => (
          <Grid item xs={12} sm={6} md={4} key={p.id}>
            <Card>
              <CardActionArea onClick={() => navigate(`/projects/${p.id}/api-keys`)}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <VpnKeyIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    <Typography variant="h6">{p.name}</Typography>
                  </Stack>
                  <Chip label={p.environment} size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t.key}: {p.publicKey}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t.retention}: {p.retentionDays}{t.days} · {t.sampling}: {Math.round(p.samplingRate * 100)}%
                    {/* The mode only changes anything below 100%, so naming it at
                        100% would be noise on every card that has not been tuned. */}
                    {p.samplingRate < 1 &&
                      ` (${p.samplingMode === 'user' ? t.perVisitor : t.perSession})`}
                  </Typography>
                  {/* A list that is actively excluding people is worth seeing from
                      here. Otherwise "why is this project quiet" is answered three
                      screens away. */}
                  {(p.userRuleMode !== 'off' || p.ipRuleMode !== 'off') && (
                    <Chip label={t.restricted} size="small" color="warning" variant="outlined" sx={{ mt: 0.5 }} />
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t.dialogTitle}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label={t.projectName}
            fullWidth
            margin="normal"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{common.cancel}</Button>
          <Button variant="contained" onClick={create} disabled={!name || createProject.isPending}>
            {common.create}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
